// src/hooks/useMyBattleList.js
import { useEffect, useState, useCallback, useRef } from "react";
import { listMyBattle } from "../api/battleApi";

import { listCoursePathPoints } from "../api/courseApi";
import { buildGoogleStaticMapUrl } from "../utils/googleStaticMap";

// statusFilter : null | 0 | 1 | 2
export default function useMyBattleList({ userId, statusFilter }) {
  const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_STATIC_MAP_KEY;

  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ 코스별 썸네일 캐시
  const thumbCacheRef = useRef(new Map());

  const getCourseThumb = async (courseId) => {
    if (!GOOGLE_KEY || !courseId) return null;

    if (thumbCacheRef.current.has(courseId)) {
      return thumbCacheRef.current.get(courseId);
    }

    try {
      const points = await listCoursePathPoints(courseId);
      if (!points || points.length === 0) {
        thumbCacheRef.current.set(courseId, null);
        return null;
      }

      const url = buildGoogleStaticMapUrl(points, {
        apiKey: GOOGLE_KEY,
        size: "200x200",
        zoom: 15,
        maxPoints: 120,
      });

      const finalUrl = url || null;
      thumbCacheRef.current.set(courseId, finalUrl);
      return finalUrl;
    } catch {
      thumbCacheRef.current.set(courseId, null);
      return null;
    }
  };

  const fetchList = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await listMyBattle({ userId, statusFilter });
      const rawList = res.data || [];

      // ✅ 1) 기본 정규화
      const normalizedBase = rawList.map((b) => ({
        ...b,
        courseImage: b.courseImage ?? b.courseImages ?? null,
      }));

      // ✅ 2) 없으면 썸네일 생성
      const normalizedWithThumb = await Promise.all(
        normalizedBase.map(async (b) => {
          if (b.courseImage) return b;
          const thumb = await getCourseThumb(b.courseId);
          return { ...b, courseImage: thumb };
        })
      );

      setBattles(normalizedWithThumb);
    } catch (err) {
      console.error("❌ useMyBattleList error", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId, statusFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { battles, loading, error, refetch: fetchList };
}
